// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import React, { useState, useRef, useCallback } from "react"

import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText, { IDtvTextProps } from "@directv-digital/dds.ui.dtv-text";
import { EqualHeight, EqualHeightElement } from "react-equal-height/clean";
import DtvButton, { IDtvButtonProps } from "@directv-digital/dds.ui.dtv-button";
import { LeftIcon, RightIcon, PlayIcon, PauseIcon } from '@directv-digital/fusion.atoms.icons';
import Button from '@directv-digital/fusion.atoms.button';
import ErrorDisplay from '@directv-digital/dds.ui.error-display';
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";
import { useBreakPoints } from "@directv-digital/dds.utils";
import Rating from '@mui/material/Rating';
import IconButton from "@mui/material/IconButton";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";

// import { colorPalette } from "@directv-digital/dds.env-theme";

export const XS_SMALL_SCREEN = "@media (min-width: 0px) and (max-width: 600px)";
export const SMALL_SCREEN_VERTICAL = "@media (min-width: 429px) and (max-width: 539px)";
export const SMALL_TABLET_SCREEN_VERTICAL = "@media (min-width: 600px) and (max-width: 1099px)";
export const SMALL_SCREEN = "@media (min-width: 0px) and (max-width: 600px)";
export const TABLET_SCREEN = "@media (min-width: 600px) and (max-width: 1099px)";
export const SMALL_DESKTOP_SCREEN = "@media (min-width: 1099px) and (max-width: 1699px)";
export const SMALL_LAPTOP_DESKTOP_SCREEN = "@media (min-width: 1099px)";

type OrientationType = "h" | "v"

export type Mode = "dark" | "light";

export interface ReviewCarouselProps {
  /* To update component id */
  id?: string;
  /* To add carousal images and content from OV api. Not CI authorable field. */
  cards: ReviewCarouselCard[];
  /* To add heading(s) above the banner(s), uses DtvText modal */
  header?: any;
  /* To add subHeading(s) directly above the banner(s), uses DtvText modal */
  subHeadings?: any;
  /* To add background color to the component */
  backgroundColor?: string;
  /* To add the font color to content */
  color?: string;
  /* TO add orientation to carousal images. Acceptable values are h (horizontal) and v(vertical).*/
  orientation?: OrientationType;
  heading?: IDtvTextProps;
  legalText?: IDtvTextProps;
  cta?: IDtvButtonProps;
  cardBackgroundColor?: string;
  mode?: Mode;
  useAnimation?:boolean;
}

interface ReviewCarouselCard {
  // can be string also
  description?: IDtvTextProps;
  author?: IDtvTextProps;
  heading?: IDtvTextProps;
  rating?:any;
  icon?: any;
}

export const onDarkTextColor = colorPalette.jet.main
export const textColorSeconday = colorPalette.gray[300]

const styles = {
  carouselHeader: {
    textAlign: "center",
    fontFamily: "PF DIN Text Pro",
    fontStyle: "normal",
    fontSize: "24px",
    fontWeight: "500",
    lineHeight: "130%", /* 26px */
    letterSpacing: "-0.4px"
  },
  carouselDescription: {
    textAlign: "center",
    fontFamily: "PF DIN Text Pro",
    fontSize: "20px",
    fontWeight: "400",
    lineHeight: "130%", /* 26px */
    letterSpacing: "-0.4px"

  },
  cardControlButtonStyles: {
    p: "4px 8px 4px 8px",
    borderRadius: "50%",
    width: "36px",
    height: "36px",
    background: "#308AFF14"
  }
}

const getColorMode = (color: string): "dark" | "light" => {
  // Remove the "#" if present
  const hex = color.replace("#", "");

  // Parse the RGB values from the hex color
  const r = parseInt(hex.substring(0, 2), 16); // Red
  const g = parseInt(hex.substring(2, 4), 16); // Green
  const b = parseInt(hex.substring(4, 6), 16); // Blue

  // Calculate the relative luminance
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Return "dark" if luminance is below the threshold, otherwise "light"
  return luminance < 128 ? "dark" : "light";
};

export function ReviewCarousel(props: ReviewCarouselProps) {
  // Generated by Copilot- Start - 2026-03-31 18:40:41
  /**
   * Replaces the old useMediaQuery calls:
   *   isXsScreen = useMediaQuery("(max-width: 600px)")        → w < 600   (mobile)
   *   isSmScreen  = useMediaQuery("(max-width: 600px)")        → w < 600   (same as above, was redundant)
   *   isTablet    = useMediaQuery(TABLET_SCREEN)               → 600 ≤ w ≤ 1099  (tablet range only)
   *   isSm        = useMediaQuery(MEDIAQUERY_TABLET)           → was never used anywhere, removed
   */
  const { isMobile, isTablet } = useBreakPoints({ tablet: 1100 });
  const isTabletOrBelow = isMobile || isTablet;
  const isDesktopUp = !isTabletOrBelow;
  // Generated by Copilot- End - 2026-03-31 18:40:41

  try {
    const { heading, cards, legalText, cta } = props
    const [scrollLeft, setScrollLeft] = useState(0);
    const [maxScrollLeft, setMaxScrollLeft] = useState(0);
    const cardRefs = useRef([]);
    const carouselRef = useRef<HTMLDivElement>(null);
    cardRefs.current = [];

    /* Not to break the existing functionality*/
    const backgroundColor = props?.cardBackgroundColor?.toUpperCase() === '#FFFFFF' ? '#21528C' : '#FFFFFF';
    const cardBackgroundColor = backgroundColor === '#FFFFFF' ? '#21528C' : '#FFFFFF';

    const backgroundColorAll = props.cardBackgroundColor || props.backgroundColor; // for Animated Carousel
    const fontColor = props.useAnimation ? (getColorMode(backgroundColorAll) === "dark" ? "#ffffff" : onDarkTextColor) :
      (props?.cardBackgroundColor?.toUpperCase() === '#FFFFFF' ? '#000000' : '#FFFFFF');

    /* */
    const [activeIndex, setActiveIndex] = useState(0)
    const [isPaused, setIsPaused] = useState(false)
    const [delayAfterClick, setDelayAfterClick] = useState(false)
    const getCarousel = () => document.querySelector(`#${props.id}-card-container`);

    const updateHeightforCarousel = () => {
      const container = document.getElementById(`${props.id}-card-container`);
      if (!container || cardRefs.current.length === 0) {
        return;
      }

      const heights = cardRefs.current.map(card => card?.scrollHeight || 0);
      const maxHeight = Math.max(...heights);

      container.style.height = `${maxHeight}px`;
    };

    React.useEffect(() => {
      const carousel = document.querySelector(`#${props.id}-card-container`);
      const handleResize = () => {
        if (props?.useAnimation) {
          if (carousel) {
            updateHeightforCarousel()
          }
        } else if (carousel) {
          const maxScroll = carousel.scrollWidth - carousel.clientWidth;
          setMaxScrollLeft(maxScroll);
          setScrollLeft(carousel.scrollLeft);
        }
      };
      if (typeof window === 'undefined') {
        return;
      }
      handleResize();
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [cards, props.id]);

    React.useEffect(() => {
      if (isPaused || delayAfterClick || !props.useAnimation) {
        return;
      }
      const interval = setInterval(() => {
        if(props.useAnimation){
          setActiveIndex(prev => (prev + 1) % cards.length)
        }
      }, 5000)
      return () => clearInterval(interval)
    }, [isPaused, delayAfterClick])

    const triggerDelay = () => {
      setDelayAfterClick(true);
      setTimeout(() => {
        setDelayAfterClick(false)
      }, 10000)
    }
    const handleNext = () => {
      setActiveIndex(prev => (prev + 1) % cards.length)
      triggerDelay()
    }
    const handlePrev = () => {
      setActiveIndex(prev => (prev - 1 + cards.length) % cards.length)
      triggerDelay()
    }
    const togglePause = () => {
      setIsPaused(prev => !prev)
    }
    const handleMouseHover = e => {
      if(isInCenter(e) && !isPaused){
        setIsPaused(true)
      }
    }
    const handleClick = (_e: any, arrowClicked: any) => {
      if(props.useAnimation){
        if (arrowClicked === "right") {
          handleNext()
        } else {
          handlePrev()
        }
      } else {
        const carousel = getCarousel();
        if (!carousel) {
          return;
        }

        // BATCH ALL READS FIRST (before any writes)
        const cardElement = document.querySelectorAll(`#${props.id} .card`)?.[0];
        const cardWidth = (cardElement?.clientWidth || 0) + 12;
        const currentScrollLeft = carousel.scrollLeft;
        const carouselScrollWidth = carousel.scrollWidth;
        const carouselClientWidth = carousel.clientWidth;
        const maxScroll = carouselScrollWidth - carouselClientWidth;

        // Calculate new scroll position using cached values
        const newScrollLeft = arrowClicked === "left" ?
          Math.max(currentScrollLeft - cardWidth, 0) :
          Math.min(currentScrollLeft + cardWidth, maxScroll);

        // BATCH ALL WRITES (after reads are done)
        carousel.scrollLeft = newScrollLeft;

        // Update state with calculated values (no DOM re-read)
        setMaxScrollLeft(maxScroll);
        setScrollLeft(newScrollLeft);
      }
    };
    // Throttled scroll handler to prevent excessive re-renders during scroll
    const handleScroll = useCallback(() => {
      const carouselEl = carouselRef.current;
      if (!carouselEl) {
        return;
      }
      const maxScroll = carouselEl.scrollWidth - carouselEl.clientWidth;
      setMaxScrollLeft(maxScroll);
      setScrollLeft(carouselEl.scrollLeft);
    }, []);

    // Throttle wrapper - limits execution to once per animation frame
    const throttledScrollHandler = useCallback(() => {
      let ticking = false;
      return () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      };
    }, [handleScroll]);

    const onScrollHandler = useRef(throttledScrollHandler()).current;
    const isInCenter = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): boolean => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left; // x position within the element.
      const y = e.clientY - rect.top;  // y position within the element.
      return (
        x > rect.width * 0.25 &&
        x < rect.width * 0.75 &&
        y > rect.height * 0.20 &&
        y < rect.height * 0.80
      )
    }
    const reviewCarouselBodyAnimated = () => (
        <EqualHeight>
          <DtvBox
            id="carddescriptioncontainer"
            sx={{
              display: "flex",
              position: "relative",
              flexDirection: "column",
              margin: "auto",
              height: "auto",
              width: "100%",
              minHeight: "350px" // Ensure height is defined
            }}
          >
            <div onMouseOver={handleMouseHover}>
              <DtvBox
                id={`${props.id}-card-container`}
                sx={{
                  position: "relative",
                  width: "100%",
                  minHeight: {
 xs: "250px",
sm: "250px",
md: "250px"
}, // Set fixed height
                  overflow: "hidden",
                  height: "auto"
                }}

              >
                {cards?.map((item, index) => <DtvBox
                    ref={el => {
                      if (el) {
 cardRefs.current[index] = el;
}
                    }}
                    key={index}
                    className="card"
                    sx={{
                      textAlign: "center",
                      backgroundColor: backgroundColorAll,
                      borderRadius: "10px",
                      width: "100%",
                      height: "auto", // Fill parent height
                      opacity: index === activeIndex ? 1 : 0,
                      visibility: index === activeIndex ? "visible" : "hidden",
                      transition: "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      flex: 1

                    }}>
                    <DtvBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <DtvBox sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minWidth: 0
                      }}>
                        {item?.heading && (
                          <DtvBox sx={{
 margin: {
 xs: "16px 0px 0px 16px",
sm: "27px 0px 0px 23px",
md: "20px 0px 0px 20px"
}
}}>
                            <DtvText
                              variant="subhead2medium"
                              content={item.heading?.content}
                              sx={{
                                alignSelf: "stretch",
                                textAlign: "center",
                                fontWeight: "400",
                                ...item.heading?.sx,
                                ...styles.carouselHeader,
                                color: `${fontColor}!important`

                              }}
                            />
                          </DtvBox>
                        )}
                        <DtvBox sx={{
 margin: {
 xs: "2px 0px 0 13px",
sm: "10px 0px 0 20px"
}
}}>
                          {item?.rating && (
                            <Rating
                              name="read-only"
                              readOnly
                              precision={0.5}
                              defaultValue={parseFloat(item.rating?.starRating) || 0}
                              sx={{
                                '& .MuiRating-iconFilled': {
                                  color: item.rating?.iconFillColor // filled stars
                                },
                                '& .MuiRating-iconEmpty': {
                                  color: item.rating?.iconEmptyColor // empty stars
                                }
                              }}
                            />
                          )}
                        </DtvBox>
                      </DtvBox>

                    </DtvBox>
                    <DtvBox sx={{
                      flex: 1,
display: "block",
flexDirection: "column",
height: "auto"
                    }}>
                      <EqualHeightElement name="review-card-description">
                        <DtvText
                          variant={"body1"}
                          data-testid={"carouseldescriptiontestId"}
                          content={`"${item?.description?.content}"`}
                          id="carouseldescriptionId"
                          sx={{
                            borderRadius: "10px",
                            width: "85%",
                            textAlign: "center",
                            letterSpacing: "0px",
                            margin: "auto",
                            opacity: "1",
                            ...item.description?.sx,
                            ...styles.carouselDescription,
                            color: `${fontColor}!important`
                          }}
                        />
                      </EqualHeightElement>
                    </DtvBox>

                    <DtvBox
                      sx={{
                        flex: 1,
display: "block",
flexDirection: "column",
minHeight: 0,
                        minWidth: 0,
                        textAlign: "center",
                        height: "auto",
                        m: "auto",
                        margin: {
                          xs: "0 15px 15px",
                          sm: "0 24px 24px",
                          md: "0 20px 20px"
                        },
                        "& .custom-text": {
                          color: `${fontColor}!important` // Scoped to this specific instance
                        }
                      }}>
                      <EqualHeightElement name="review-card-author">
                        <DtvText
                          className="custom-text"
                          variant={"body1"}
                          height={"10px"}
                          sx={{
                            ...(item?.author?.sx || {})
                          }}
                          {...item?.author} />
                      </EqualHeightElement>
                    </DtvBox>
                    <DtvBox
                      sx={{
                        m: "auto"
                      }}>
                      <EqualHeightElement name="review-card-logo">
                        {(item?.heading || item?.rating) && (
                          <DtvBox sx={{
                            ml: 1,
display: "flex",
alignItems: "center"
                          }}>
                            {item?.icon?.url && (
                              <img
                                src={item?.icon?.url}
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  margin: "auto"
                                }}
                              />
                            )}
                          </DtvBox>
                        )}
                      </EqualHeightElement>
                    </DtvBox>
                  </DtvBox>)}
              </DtvBox>
            </div>
            {cards.length > 1 && (
              <DtvBox
                id={"carouselControlBtn"}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  height: "auto",
                  padding: {
 xs: "0 0px",
sm: "0 32px",
md: "0 140px"
},
                  width: "100%",
                  marginTop: "2px",
                  position: "relative"
                }}
              >
                <IconButton
                  sx={{
                    ...styles.cardControlButtonStyles
                  }}
                  onClick={(_e: any) => togglePause()}
                  data-testid="pause-play-button"
                  aria-label={isPaused ? "Play carousel" : "Pause carousel"}
                >
                  {isPaused ? <PlayIcon fill={resolvedMode === "dark" ? "#FFFFFF" : "#0062E3"} /> : <PauseIcon fill={resolvedMode === "dark" ? "#FFFFFF" : "#0062E3"} />}
                </IconButton>
                <DtvBox
                  id={"carouselLeftRightbtn"}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "16px",
                    width: "100%",
                    position: "relative",
                    alignItems: "center"
                  }}
                >
                  <IconButton
                    sx={{
                      ...styles.cardControlButtonStyles
                    }}
                    data-testid="previousBtn"
                    onClick={(e: any) => handleClick(e, "left")}
                    aria-label="previous slide"
                  >
                    <LeftIcon stroke={resolvedMode === "dark" ? "#FFFFFF" : "#0062E3"}
                      fill={resolvedMode === "dark" ? "#FFFFFF" : "#0062E3"}
                    />

                  </IconButton>
                  <IconButton
                    sx={{
                      ...styles.cardControlButtonStyles
                    }}
                    data-testid="nextBtn"
                    onClick={(e: any) => handleClick(e, "right")}
                    aria-label="next slide"
                  >
                    <RightIcon stroke={resolvedMode === "dark" ? "#FFFFFF" : "#0062E3"}
                      fill={resolvedMode === "dark" ? "#FFFFFF" : "#0062E3"}
                    />
                  </IconButton>
                </DtvBox>
              </DtvBox>
            )}
          </DtvBox>
        </EqualHeight>

    )
    const reviewCarouselBodyDefault = () => (<EqualHeight>
        <DtvBox sx={{
          display: "flex",
          position: "relative",
          marginLeft: {
            xs: "20px",
            sm: "36px",
            md: "36px",
            lg: "97px",
            xl: "97px"
          },
          transition: "margin 300ms ease",
          flexDirection: "column"
        }}>
          <DtvBox
            ref={carouselRef}
            id={`${props.id}-card-container`}
            sx={{
              display: "flex",
              gap: {
                xs: '21px',
                sm: '28px',
                md: '35px'
              },
              overflowX: "auto",
              scrollSnapType: "x proximity",
              scrollbarWidth: "none",
              scrollBehavior: 'smooth',
              "&::-webkit-scrollbar": {
                width: "0px",
                background: "transparent"
              },
              justifyContent: {
                xs: "flex-start",
                sm: cards.length === 2 ? "center" : "flex-start"
              }
            }}
            onScroll={onScrollHandler}
          // eslint-disable-next-line react/jsx-no-comment-textnodes
          >
            {cards?.map((item, itemIdx) => <DtvBox
                key={itemIdx}
                className="card"
                tabIndex={0}
                sx={{
                  backgroundColor: props?.cardBackgroundColor ? props?.cardBackgroundColor : cardBackgroundColor,
                  borderRadius: "10px",
                  width: "100%",
                  minHeight: {
 xs: "365px",
sm: "285px",
md: "322px"
},
                  opacity: "1",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative"
                }}>
                <DtvBox
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "flex-start"
                  }}
                >
                  <DtvBox sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0
                  }}>
                    {item?.heading && (
                      <DtvBox sx={{
 margin: {
 xs: "16px 0px 0px 16px",
sm: "27px 0px 0px 23px",
md: "20px 0px 0px 20px"
}
}}>
                        <DtvText
                          variant="subhead2medium"
                          content={item.heading?.content}
                          sx={{
                            fontWeight: "500",
                            ...item.heading?.sx
                          }}
                        />
                      </DtvBox>
                    )}
                    <DtvBox sx={{
 margin: {
 xs: "2px 0px 0 13px",
sm: "10px 0px 0 20px"
}
}}>
                      {item?.rating && (
                        <Rating
                          name="read-only"
                          readOnly
                          precision={0.5}
                          defaultValue={parseFloat(item.rating?.starRating) || 0}
                          sx={{
                            fontSize: {
 xs: "1.5rem !important",
sm: "1.7rem !important"
},
                            '& .MuiRating-iconFilled': {
                              color: item.rating?.iconFillColor // filled stars
                            },
                            '& .MuiRating-iconEmpty': {
                              color: item.rating?.iconEmptyColor // empty stars
                            }
                          }}
                        />
                      )}
                    </DtvBox>
                  </DtvBox>
                  {(item?.heading || item?.rating) && (
                    <DtvBox sx={{
                      ml: 2,
display: "flex",
alignItems: "flex-start",
padding: "20px"
                    }}>
                      {item?.icon?.url && (
                        <img
                            alt={item?.icon?.alt || "review-logo"}
                          src={item?.icon?.url}
                          style={{
                            width: "56px",
                            height: "56px"
                          }}
                        />
                      )}
                    </DtvBox>
                  )}
                </DtvBox>
                <DtvBox sx={{
                  flex: 1,
display: "flex",
flexDirection: "column",
minHeight: 0
                }}>
                  <EqualHeightElement name="review-card-description">
                    <DtvText
                      variant={"body1"}
                      content={item?.description?.content}
                      sx={{
                        borderRadius: "10px",
                        width: {
                          xs: "191px",
                          sm: "278px",
                          md: "323px"
                        },
                        textAlign: "left",
                        letterSpacing: "0px",
                        margin: {
                          xs: "15px 15px 0px 15px",
                          sm: "12px 24px 0px 24px",
                          md: "10px 26px 0px 26px"
                        },
                        opacity: "1",
                        color: fontColor,
                        ...item.description?.sx
                      }}
                    />
                  </EqualHeightElement>
                </DtvBox>
                <DtvBox
                  sx={{
                    textAlign: "right",
                    mt: "auto",
                    margin: {
                      xs: "0 15px 15px",
                      sm: "0 24px 24px",
                      md: "0 20px 20px"
                    }
                  }}>
                  <EqualHeightElement name="review-card-author">
                    <DtvText
                      variant={"body1"}
                      sx={{
                        color: fontColor,
                        ...(item?.author?.sx || {})
                      }}
                      {...item?.author} />
                  </EqualHeightElement>
                </DtvBox>
              </DtvBox>)}
          </DtvBox>
          {cards.length > 2 && (
            <DtvBox
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "16px",
                width: "100%",
                marginTop: {
 xs: 2,
md: "28px"
},
                position: "relative",
                left: { md: "-60px" }
              }}
            >
              <Button
                id={`${props?.id}-left`}
                className="navButtons"
                onClick={(e: any) => handleClick(e, "left")}
                buttontype="tertiary"
                mode={resolvedMode}
                size="large"
                iconOnly={true}
                icon={
                  // @ts-expect-error DDS Icon component has stricter prop typing than actual usage
                  <LeftIcon mode={resolvedMode} />
                }
                aria-label={"scroll the carousel left"}
                googleanalyticsdata={{
                  id: `${props?.id}-left-chevron`,
                  position: "left"
                }}
                sx={{
                  minWidth: "unset !important",
                  padding: "0px !important",
                  margin: "0px !important",
                  display: "flex",
                  "& svg": {
                    height: "36px",
                    width: "36px"
                  },
                  position: "static",
                  top: "unset",
                  transform: "none",
                  opacity: scrollLeft === 0 ? 0.4 : 1,
                  pointerEvents: scrollLeft === 0 ? "none" : "auto"
                }}
              ></Button>
              <Button
                id={`${props?.id}-right`}
                className="navButtons"
                onClick={(e: any) => handleClick(e, "right")}
                buttontype="tertiary"
                mode={resolvedMode}
                size="large" // L:24, M:20, S:16
                iconOnly={true}
                icon={
                  // @ts-expect-error DDS Icon component has stricter prop typing than actual usage
                  <RightIcon mode={resolvedMode} />
                }
                aria-label={"scroll the carousel right"}
                googleanalyticsdata={{
                  id: `${props?.id}-right-chevron`,
                  position: "right"
                }}
                sx={{
                  minWidth: "unset !important",
                  padding: "0px !important",
                  margin: "0px !important",
                  display: "flex",
                  "& svg": {
                    height: "36px",
                    width: "36px"
                  },
                  opacity: Math.ceil(scrollLeft) >= maxScrollLeft - 5 ? 0.4 : 1,
                  pointerEvents: Math.ceil(scrollLeft) >= maxScrollLeft - 5 ? "none" : "auto",
                  position: "static",
                  right: isDesktopUp ? "-90px" : "-170px"
                }}
              ></Button>
            </DtvBox>
          )}
        </DtvBox>
      </EqualHeight>

    )

    const resolvedMode =
      props?.mode === "dark" || props?.mode === "light" ?
        props.mode :
        getColorMode(backgroundColorAll);
    return <DtvBox id={props.id}
      sx={{
        background: props.useAnimation ? backgroundColorAll : props?.backgroundColor,
        borderRadius: "10px",
        margin: {
          xs: "0 20px 0 20px",
          sm: "0 32px 0 32px",
          md: "0 140px 0 140px",
          lg: "0 140px 0 140px"
        }
      }}>

      {heading?.content &&
        <DtvBox sx={{
          display: 'flex',
          width: '100%',
          justifyContent: "center",
          textAlign: "center",
          px: {
            xs: "0 15px 0 12px",
            sm: "0 1px 0 20px",
            md: "0 38px 0 65px"
          }
        }}>
          <DtvText
            content={heading?.content}
            variant={heading?.variant || "subhead1"}
            sx={{
              color: heading?.color,
              textAlign: 'center',
              width: "100%",
              ml: "inherit",
              margin: {
                xs: "40px 0 16px 0",
                sm: "48px 0 24px 0",
                md: "60px 0 28px 0"
              }
            }}
          />
        </DtvBox>
      }
      {props.useAnimation ? reviewCarouselBodyAnimated() : reviewCarouselBodyDefault()}
      {props?.cta?.label && (
        <DtvBox
          sx={{
            display: "flex",
            justifyContent: "center",
            margin: {
              xs: "24px 0 0 0",
              sm: "24px 0 0 0",
              md: "28px 0 0 0"
            },
            px: 0,
            width: "100%",
            label: props?.cta?.label
          }}
        >
          <DtvButton
            useParsedLabel={true}
            googleAnalyticsData={getAnalyticsAttrs(cta?.label, "panel_seasonal favorites & more", cta?.targeturl)}
            {...cta}
          />
        </DtvBox>
      )}

      <DtvBox
        sx={{
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
          color: heading?.color,
          width: '100%'
        }}
      >
        {legalText?.content &&
          <DtvText
            variant={"smallText"}
            color={heading?.color}
            sx={{
              margin: {
                xs: "24px 0 40px 0",
                sm: "24px 0 48px 0",
                md: "28px 0 60px 0"
              }
            }}
            {...legalText}
          />
        }
      </DtvBox>

    </DtvBox>
  } catch (error) {
    return <ErrorDisplay error={error} />;
  }
}
